// Package imports:
import React, { useMemo, useRef } from 'react';
import { Formik } from 'formik';
import Bugsnag from '@bugsnag/js';
import cx from 'classnames';
// Component imports:
import Modal from '../../../ui-elements/Modal/Modal';
import { AddCompanyForm, validate } from './AddCompanyForm';
// Service imports:
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { AddCompanyFormValues, AddMultipleCustomersFeedback, BatchOperation, BatchOperationResult, IDueDiligenceModalProps, KnowYourCustomer, ServiceCompanyEmployee } from '../../../types/DueDiligenceTypes';
import Link from '../../../ui-elements/Link/Link';
import FileUploadComponent, { ValidationError } from '../../../ui-elements/ImportButton/ImportButton';
import Table from '../../../ui-elements/Table/Table';
import Button from '../../../ui-elements/Button/Button';
import { getIcelandicWord, insertDash } from '../../../services/utils';
import ProgressBar from '../../../ui-elements/ProgressBar/ProgressBar';
import Alert from '../../../ui-elements/Alert/Alert';

// Define the structure of each row in the uploaded file
export interface UploadedEntity {
    kennitala: string;
    netfang?: string;   // used for individuals
    netfangUmsjonaradila?: string;
    kennitalaTengiliðs?: string;
    netfangTengiliðs?: string;
    error?: ValidationError[];
}
// Props for the AddCompanyModal component
interface IProps extends IDueDiligenceModalProps {
    employees: ServiceCompanyEmployee[]; // needed for the supervisor dropdown
    onAddCustomer: (customer: KnowYourCustomer) => void; // callback to add a new customer
}

const MIN_VISIBLE_ERRORS = 6;
interface IUploadSuccess {
    uploadSuccess: boolean | null;
    uploadedCount: number;
}

// Main component for adding a company via modal
const AddCompanyModal: React.FC<IProps> = ({
    show,
    close,
    setToastShow,
    setToastType,
    setToastMessage,
    employees,
    onAddCustomer
}) => {
    const [disableSubmit, setDisableSubmit] = React.useState(false);
    const [parsingState, setParsingState] = React.useState<"idle" | "parsing" | "completed" | "submitting">("idle");
    const [uploadedEntities, setUploadedEntities] = React.useState<UploadedEntity[]>([]);
    const [progress, setProgress] = React.useState<{ processedRows: number; totalRows: number }>({ processedRows: 0, totalRows: 0 });
    const [errors, setErrors] = React.useState<ValidationError[]>([]);
    const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
    const [visibleErrorCount, setVisibleErrorCount] = React.useState(MIN_VISIBLE_ERRORS);
    //number for 
    const [uploadSuccess, setUploadSuccess] = React.useState<IUploadSuccess>({ uploadSuccess: null, uploadedCount: 0 });

    const handleClose = () => {
        setParsingState("idle");
        setErrors([]);
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);
        setUploadedEntities([]);
        setUploadSuccess({ uploadSuccess: null, uploadedCount: 0 });
        setVisibleErrorCount(MIN_VISIBLE_ERRORS);
        close();
    }

    // Callbacks for file upload progress
    const onProcessingStart = () => {
        setDisableSubmit(true);
        setParsingState("parsing");
    }
    const onProgressUpdate = (processedRows: number, totalRows: number) => {
        setProgress({ processedRows, totalRows });
    }
    const onProcessingComplete = () => {
        setDisableSubmit(false);
        setParsingState("completed");
    }

    // Handles form submission for adding a company
    const handleSubmit = async (values: AddCompanyFormValues) => {
        try {
            setDisableSubmit(true);
            // Submit form data to backend
            const response = await fetch('/DueDiligence/AddNewCompany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            setDisableSubmit(false);

            if (!response.ok) {
                const errorData = await response.text();
                setToastType("error");
                setToastMessage(errorData || 'Ekki tókst að bæta við lögaðila');
                setToastShow(true);
                return;
            }

            // set the newly added company in the parent component
            const newCustomer: KnowYourCustomer = await response.json();
            onAddCustomer(newCustomer);
            setToastType("success");
            setToastMessage(newCustomer.companyName ? `${newCustomer.companyName} hefur verið bætt við.` : 'Lögaðila hefur verið bætt við.');
            setToastShow(true);
            handleClose(); // Close modal if it was open

        } catch (err: any) {
            setDisableSubmit(false);
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage(ErrorMessages.OtherShort);
            setToastShow(true);
        }
    };

    const cancelUpload = () => {
        setErrors([]);
        setParsingState("idle");
        setUploadedEntities([]);
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);
    }

    // Reference to the abort function from FileUploadComponent
    // This will be populated by the FileUploadComponent and allows us to trigger abort
    const abortRef = useRef<(() => void) | null>(null);
    /**
     * Called when file processing is aborted by user
     * Resets processing state and clears any partial results
     */
    const onProcessingAborted = () => {
        setParsingState("idle");
        setUploadedFile(null);
        setProgress({ processedRows: 0, totalRows: 0 });
        setDisableSubmit(false);

        // Clear any partial results from the aborted upload
        setUploadedEntities([]);
        setErrors([]);
    };

    /**
     * Handles the abort button click
     * Calls the abort function exposed by FileUploadComponent
     */
    const handleAbort = () => {
        if (abortRef.current) {
            abortRef.current();
        }
    };

    const subFormComponent = useMemo(() => {
        return (props: { values: AddCompanyFormValues }) => (
            <AddCompanyForm values={props.values} employees={employees} closeModal={handleClose} disableSubmit={disableSubmit} />
        )
    }, [disableSubmit]);

    /**
     * Fetches the final results of a completed batch operation.
     * This should be called after polling confirms the batch has completed.
     * 
     * @param batchId - The unique identifier for the batch operation
     * @returns Promise containing the final results with any errors or successfully processed customers
     * @throws Error if the API request fails
     */
    const fetchBatchResults = async (batchId: number): Promise<BatchOperation> => {
        try {
            // Call the batch results endpoint to get the final outcome
            const response = await fetch(`/Batch/GetBatchStatusResults/${batchId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Check if the API call was successful
            if (!response.ok) {
                throw new Error(`Failed to get batch results: ${response.statusText}`);
            }

            // Parse and return the final results
            const results: BatchOperation = await response.json();

            return results;

        } catch (error) {
            console.error('Error fetching batch results:', error);
            throw error; // Propagate the error to the caller
        }
    };

    /**
  * Polls the batch status endpoint to check the progress of a batch operation.
  * This function will continuously check the status until the batch is completed.
  * Includes safeguards to detect when the backend is stuck returning identical results.
  * 
  * @param batchId - The unique identifier for the batch operation to monitor
  * @param onProgress - Optional callback function to receive progress updates
  * @returns Promise that resolves when the batch operation is complete
  * @throws Error if polling times out, API request fails, or identical results are returned too many times
  */
    const pollBatchStatus = async (
        batchId: number,
        onProgress?: (operation: BatchOperation) => void
    ): Promise<void> => {
        // Maximum number of polling attempts to prevent infinite loops
        // With 1 second intervals, this allows up to 5 minutes of processing time
        const maxAttempts = 300;
        // Maximum number of times we can receive the same result before considering it stuck
        const maxIdenticalResults = 8;

        let attempts = 0;

        // Variables to track identical consecutive results
        let previousCount: number | null = null;
        let identicalResultCount = 0;

        // Continue polling until we get a complete status or reach max attempts
        while (attempts < maxAttempts) {
            try {
                // Call the batch status API endpoint to get current operation state
                const response = await fetch(`/Batch/GetBatchStatus/${batchId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                // Check if the API call was successful
                if (!response.ok) {
                    throw new Error(`Failed to get batch status: ${response.statusText}`);
                }

                // Parse the response data into our BatchOperation interface
                const operation: BatchOperation = await response.json();

                // Check if we're receiving the same result repeatedly (backend might be stuck)
                if (previousCount !== null && operation.count === previousCount) {
                    identicalResultCount++;

                    // If we've received the same result too many times, the backend is likely stuck
                    if (identicalResultCount >= maxIdenticalResults) {
                        throw new Error(
                            `Backend appears to be stuck: received identical result (count: ${operation.count}) ${identicalResultCount} times consecutively`
                        );
                    }
                } else {
                    // Progress has been made, reset the identical result counter
                    identicalResultCount = 0;
                }

                // Update the previous count for next iteration comparison
                previousCount = operation.count;

                // Notify the caller about the current progress if callback provided
                // This allows the UI to update with real-time progress information
                if (onProgress) {
                    onProgress(operation);
                }

                // Check if the batch operation has completed processing
                if (operation.complete) {
                    return; // Exit the polling loop - batch is done
                }

                // Use shorter timeout for first few attempts, then longer intervals
                const timeoutDuration = attempts < 3 ? 2000 : 5000;

                // Wait before the next polling attempt to avoid overwhelming the server
                await new Promise(resolve => setTimeout(resolve, timeoutDuration));

                attempts++;

            } catch (error) {
                console.error('Error polling batch status:', error);
                throw error; // Propagate the error to the caller so they can handle it
            }
        }

        // If we reached max attempts without completion, throw a timeout error
        throw new Error('Batch status polling timed out');
    };

    /**
     * Updated submit handler that includes batch status polling.
     * After successfully uploading the file, this function will monitor
     * the batch processing progress and handle the final results.
     * Includes error handling for cases where the backend gets stuck.
     */
    const handleSubmitUploadedEntities = async () => {
        try {
            // Initialize submitting state
            setParsingState("submitting");
            setErrors([]);
            setProgress({ processedRows: 0, totalRows: uploadedEntities.length });

            // Ensure we have a file to submit
            if (!uploadedFile) {
                console.error('No file to submit');
                return;
            }

            setDisableSubmit(true);

            // Create FormData object to send file as multipart/form-data
            const formData = new FormData();

            // Append the file with the key 'excelFile' as expected by the API
            formData.append('excelFile', uploadedFile);

            // Submit the file to the API endpoint
            const response = await fetch('/DueDiligence/AddMultipleCompanies', {
                method: 'POST',
                // DO NOT set Content-Type header - browser will set it automatically with boundary
                body: formData
            });

            const responseData: AddMultipleCustomersFeedback = await response.json();

            if (response.ok) {
                if (responseData.batchId) {
                    // File was accepted and batch processing has started
                    // Now poll for the batch status until completion
                    // This will throw an error if the backend gets stuck
                    await pollBatchStatus(
                        responseData.batchId,
                        (operation) => {
                            // Update UI with progress information
                            setProgress({ processedRows: operation.count, totalRows: operation.totalCount });
                            setTimeout(() => { }, 0); // Force UI update
                        }
                    );

                    // Once polling completes (operation.complete === true), fetch the final results
                    const resultsResponse = await fetch(`/Batch/GetBatchStatusResults/${responseData.batchId}`);

                    if (!resultsResponse.ok) {
                        throw new Error('Failed to get batch results');
                    }

                    const finalResults: BatchOperationResult = await fetchBatchResults(responseData.batchId);

                    // CHECK FOR ERRORS IN FINAL RESULTS
                    const hasErrors = finalResults.items?.filter(item => item.errorMessage);

                    // Handle the final results from the batch operation
                    if (finalResults.complete && hasErrors && hasErrors.length > 0) {
                        // Some customers failed to process - show errors to the user
                        setErrors([{
                            row: 0,
                            field: 'Skrá',
                            value: uploadedFile.name,
                            headerMessage: 'Eftirfarandi villur fundust við vinnslu skráar:',
                            message: <div>
                                <div>
                                    {hasErrors.map((err, idx) => {
                                        if (err.errorMessage && err.errorMessage.includes("Ástæða:")) {
                                            const [before, after] = err.errorMessage.split("Ástæða:");
                                            return (
                                                <p className='paragraph-small' key={idx}>
                                                    {before}
                                                    <b>Ástæða:</b>
                                                    {after}
                                                </p>
                                            );
                                        }
                                        return <p className='paragraph-small' key={idx}>{err.errorMessage}</p>;
                                    })}
                                </div>
                            </div >
                        }]);
                    } else if (finalResults.complete && finalResults.items?.length === finalResults.totalCount) {
                        // All customers processed successfully
                        setUploadSuccess({ uploadSuccess: true, uploadedCount: finalResults.items.length });
                    }
                    else {
                        // Unexpected state - log for investigation
                        Bugsnag.notify(new Error('Unexpected state in batch processing results'));
                        setUploadSuccess({ uploadSuccess: false, uploadedCount: finalResults.items ? finalResults.items.length : 0 });
                    }
                }
            }

            // Error handling for failed submission
            // Check for template errors (file format issues)
            if (responseData) {
                if (responseData.templateError) {
                    // Log this since the former validation should have caught it
                    Bugsnag.notify(new Error('Template error when submitting multiple companies: ' + responseData.errorMessage));
                    setErrors([{
                        row: 0,
                        field: 'Skrá',
                        value: uploadedFile.name,
                        message: 'Villa í sniðmáti skráar. Vinsamlegast notið sniðmátið hér að ofan'
                    }]);
                }
                // Check for immediate validation errors
                else if (responseData.errors && responseData.errors.length > 0) {
                    setErrors([{
                        row: 0,
                        field: 'Skrá',
                        value: uploadedFile.name,
                        headerMessage: 'Eftirfarandi villur fundust við vinnslu skráar:',
                        message: <div>
                            <div>
                                {responseData.errors.map((errorItem, idx) => {
                                    const ssn = errorItem.companySSN || 'Óþekkt';
                                    const supervisorEmail = errorItem.supervisorUsername || '';
                                    const rowNum = errorItem.rowNumber;
                                    const errorMessages = errorItem.errors || [];

                                    return (
                                        <div key={idx} className='paragraph-small' style={{ marginBottom: '12px' }}>
                                            <p style={{ marginBottom: '4px' }}>
                                                <b>Röð {rowNum}:</b> {ssn} {supervisorEmail ? `(${supervisorEmail})` : ''}
                                            </p>
                                            <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                                                {errorMessages.map((errMsg, errIdx) => (
                                                    <li key={errIdx}>{errMsg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    }]);
                }
            }
        }
        catch (err: any) {
            console.error('Error submitting file:', err);

            // Check if this is a stuck backend error (identical results received too many times)
            if (err instanceof Error && err.message.includes('Backend appears to be stuck')) {
                // Log the stuck state to Bugsnag for investigation
                Bugsnag.notify(err);

                // Display user-friendly error message in Icelandic
                setErrors([{
                    row: 0,
                    field: 'Skrá',
                    value: uploadedFile?.name || '',
                    headerMessage: 'Eitthvað fór úrskeiðis',
                    message: <span>Eitthvað fór úrskeiðis við vinnslu skráar. Vinsamlegast reynið aftur síðar eða hafið samband við þjónustudeild. <Link linkSize='16' url='mailto:info@keldan.is'>info@keldan.is</Link></span>
                }]);
            }
            // Check if this is a timeout error
            else if (err instanceof Error && err.message.includes('timed out')) {
                // Log timeout to Bugsnag
                Bugsnag.notify(err);

                // Display timeout error message
                setErrors([{
                    row: 0,
                    field: 'Skrá',
                    value: uploadedFile?.name || '',
                    headerMessage: 'Vinnsla tók of langan tíma',
                    message: 'Vinnsla skráar tók lengri tíma en búist var við. Vinsamlegast reynið aftur síðar.'
                }]);
            }
            // Generic error handling for other unexpected errors
            else {
                setErrors([{
                    row: 0,
                    field: 'Skrá',
                    value: uploadedFile?.name || '',
                    headerMessage: 'Óvænt villa',
                    message: 'Villa kom upp við að vinna úr skrá. Vinsamlegast reynið aftur.'
                }]);
            }
        }
        finally {
            // Always reset the parsing state and re-enable submit button
            setParsingState("completed");
            setDisableSubmit(false);
        }
    };

    // Renders the modal body (form)
    const renderBody = () => {
        const FILE_ERROR = errors.find(err => err.field === 'Skrá');
        const hasErrors = uploadedEntities.filter(entity => entity.error && entity.error.length > 0);
        const isPlural = uploadSuccess.uploadedCount > 1;

        if (uploadSuccess.uploadSuccess === true) {
            return <Alert
                type='success'
                headText={"Skránni hefur verið hlaðið upp"}
                text={<>
                    {`${(isPlural ? 'Alls voru' : '')} ${uploadSuccess.uploadedCount} ${(isPlural ? 'lögaðilum' : 'lögaðila')} bætt við eða breytt. Til að sjá breytinguna í töflu`}
                    <strong> þarf að endurhlaða síðuna,</strong> eða smella <Link href={window.location.pathname}>hér</Link>.
                </>}
            />;
        } else if (uploadSuccess.uploadSuccess === false) {
            return <Alert type='error' headText={"Ekki tókst að hlaða skránni upp"} text={`Eitthvað fór úrskeiðis, vinsamlegast reyndu aftur síðar.`} />;
        }
        if (parsingState === "parsing") {
            return <div><p className="paragraph">Unnið er úr skrá, vinsamlegast bíðið...</p>
                <ProgressBar current={progress.processedRows} total={progress.totalRows} />
                <Button buttonType='secondary' onClick={handleAbort}>Hætta við</Button>
            </div>;
        }
        else if (parsingState === "submitting") {
            return <div><p className="paragraph">Uppfæri lögaðila...</p>
                <ProgressBar current={progress.processedRows} total={progress.totalRows} />
            </div>;
        }

        const showUploadedEntities = () => {
            //display the uploaded entities in a table
            return <div className='KCL_add-customer-modal uploaded-entities-section'>
                <div className="instructions">
                    Ef lögaðili er þegar til, verða nýjar upplýsingar um tengilið og umsjónaraðila uppfærðar.
                </div>
                <Table
                    tableSize='sm'
                    withPagination="auto"
                    data={uploadedEntities}
                    rowClassName={({ error }) => error?.length === 0 ? '' : 'table__row--error'}
                    columns={[{
                        title: 'Kennitala',
                        renderCell: ({ kennitala }) => insertDash(kennitala),
                        textAlign: 'left',

                    }, {
                        title: 'Netfang umsjónaraðila',
                        renderCell: ({ netfangUmsjonaradila }) => netfangUmsjonaradila ? <Link linkSize='13' url={`mailto:${netfangUmsjonaradila}`}>{netfangUmsjonaradila}</Link> : '-',
                        textAlign: 'left'
                    }, {
                        title: 'Kennitala tengiliðs',
                        renderCell: ({ kennitalaTengiliðs }) => kennitalaTengiliðs ? insertDash(kennitalaTengiliðs) : '-',
                        textAlign: 'left'
                    }, {
                        title: 'Netfang tengiliðs',
                        renderCell: ({ netfangTengiliðs }) => netfangTengiliðs ? <Link linkSize='13' url={`mailto:${netfangTengiliðs}`}>{netfangTengiliðs}</Link> : '-',
                        textAlign: 'left'
                    }]} />

                {hasErrors.length > 0 &&
                    <div className='error-messages-section'>
                        <div className={cx('error-message')}>
                            <span className='paragraph-small error_overview'>
                                <span className='paragraph-small--bold'>Samantekt: </span>
                                {hasErrors.length} {getIcelandicWord(hasErrors.length, 'villa', 'villur')} {getIcelandicWord(hasErrors.length, 'fannst', 'fundust')} - vinsamlegast lagið allar villur til að senda skjal
                            </span>
                            <div className={cx('error_details_container', { 'with_scroll': hasErrors.length > MIN_VISIBLE_ERRORS && visibleErrorCount > MIN_VISIBLE_ERRORS })}>
                                {/* Show gradient overlay when there are more errors to display */}
                                {visibleErrorCount === MIN_VISIBLE_ERRORS && <div className='gradient_overlay' />}

                                {/* Display errors up to the current visible count */}
                                {hasErrors.slice(0, visibleErrorCount).map((error, index) => {
                                    // Only display this row if it has validation errors
                                    if (!error.error || error.error.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <div key={index} className='error_row_container'>
                                            {/* Display the row number followed by all field errors for that row */}
                                            <p className='error_rowNumber'>
                                                Röð {error.error[0]?.row - 1}: {/* Get the row number from the first error */}
                                            </p>
                                            <div className='error_messages_container'>
                                                {error.error.map((err, i) => (
                                                    <div key={i}>
                                                        {/* Format each error as "field - error message" */}
                                                        <span className='error_field'>{err.field}</span>
                                                        <span className='error_message'>
                                                            - {err.message}{error.error && i < error.error.length - 1 ? ', ' : ''}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Show toggle button only if there are more than 3 errors */}
                        {hasErrors.length > MIN_VISIBLE_ERRORS && (
                            <span
                                className='paragraph-xsmall showAllText'
                                onClick={() => {
                                    if (visibleErrorCount > MIN_VISIBLE_ERRORS) {
                                        // User wants to collapse - show only MIN_VISIBLE_ERRORS errors
                                        setVisibleErrorCount(MIN_VISIBLE_ERRORS);
                                    } else {
                                        // User wants to expand - show next batch (up to 100 more errors)
                                        const nextBatch = Math.min(visibleErrorCount + 100, hasErrors.length);
                                        setVisibleErrorCount(nextBatch);
                                    }
                                }}
                            >
                                {visibleErrorCount > MIN_VISIBLE_ERRORS
                                    ? 'Sýna minna'
                                    : (() => {
                                        // Calculate how many more errors can be displayed
                                        const remainingErrors = hasErrors.length - visibleErrorCount;
                                        const nextBatchSize = Math.min(remainingErrors, 100);
                                        return `Birta ${nextBatchSize} villur í viðbót`;
                                    })()
                                }
                            </span>
                        )}
                    </div>
                }

                <div className='action-button-group' style={{ marginTop: '20px' }}>
                    <Button buttonType='secondary' onClick={cancelUpload}>Hætta við</Button>
                    <Button disabled={uploadedEntities.length === 0 || hasErrors.length > 0} buttonType='primary' onClick={handleSubmitUploadedEntities}>Senda</Button>
                </div>
            </div>
        }
        return (
            <div className="KCL_add-customer-modal">
                <div className='upload-section'>
                    <p className="paragraph excel-instructions">Hægt er að bæta við eða breyta lögaðila með Excel skjali, sjá <Link icon='excel' url="/templates/add_company_template.xlsx">Sniðmát í Excel</Link></p>
                    <FileUploadComponent
                        setEntities={setUploadedEntities}
                        setErrors={setErrors}
                        onProcessingStart={onProcessingStart}
                        onProgressUpdate={onProgressUpdate}
                        onProcessingComplete={onProcessingComplete}
                        onProcessingAborted={onProcessingAborted}
                        setUploadedFile={setUploadedFile}
                        abortRef={abortRef}
                        validationType='company'
                    />
                </div>
                {parsingState === "completed" && !FILE_ERROR && showUploadedEntities()}
                {FILE_ERROR ?
                    <div className='error-message'>
                        <Alert type={'error'} headText={FILE_ERROR.headerMessage ?? 'Villa'} text={FILE_ERROR.message} />
                        <div className='action-button-group' style={{ marginTop: '20px' }}>
                            <Button buttonType='secondary' onClick={cancelUpload}>Hætta við</Button>
                        </div>
                    </div>
                    : uploadedEntities.length > 0 ? null
                        : <Formik
                            initialValues={{
                                foreign: false,
                                companySSN: "",
                                companyName: "",
                                foreignCompanyName: "",
                                foreignCompanyPostalCode: "",
                                foreignCompanyAddress: "",
                                foreignCompanyRegion: "",
                                foreignCompanyCountry: "",
                                supervisorId: null
                            }}
                            validate={validate}
                            onSubmit={handleSubmit}
                            component={subFormComponent}
                            validateOnMount={false}
                        />
                }

            </div>
        );
    };

    // Render modal and toast notification
    return (
        <Modal
            show={show}
            onHide={() => {
                handleClose();
            }}
            header={"Bæta við lögaðila"}
            body={renderBody()}
            hasCloseButton
        />
    );
};

export default AddCompanyModal;